import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
  Dispatch,
  SetStateAction,
  useCallback,
} from "react";

interface TaskContextType {
  tasks: Category[];
  currentTask: Category | null;
  setCurrentTask: Dispatch<SetStateAction<Category | null>>;
  isLoading: boolean;
  error: string | null;
  fetchTasks: (token: string) => Promise<void>;
}

const TaskContext = createContext<TaskContextType | undefined>(undefined);

interface TaskProviderProps {
  children: ReactNode;
}

export const TaskProvider: React.FC<TaskProviderProps> = ({ children }) => {
  const [tasks, setTasks] = useState<Category[]>([]);
  const [currentTask, setCurrentTask] = useState<Category | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTasks = useCallback(
    async (userToken: string) => {
      setIsLoading(true);
      setError(null);
      try {
        const apiUrl = `${process.env.REACT_APP_API_URL}/category/nested/`;
        const response = await fetch(apiUrl, {
          headers: {
            Authorization: `bearer ${userToken}`,
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch tasks");
        }
        const data: Category[] = await response.json();
        setTasks(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred",
        );
        setTasks([]); // Clear tasks on error
      } finally {
        setIsLoading(false);
      }
    },
    [setTasks, setIsLoading, setError],
  );

  const value = {
    tasks,
    currentTask,
    setCurrentTask,
    isLoading,
    error,
    fetchTasks,
  };

  return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>;
};

export const useTasks = (): TaskContextType => {
  const context = useContext(TaskContext);
  if (context === undefined) {
    throw new Error("useTasks must be used within a TaskProvider");
  }
  return context;
};
